'use client'

import { useState, useContext } from 'react'
import { Card, CardContent } from '@/components/ui/shadcn/card'
import { Button } from '@/components/ui/shadcn/button'
import { Separator } from '@/components/ui/shadcn/separator'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { CheckCircle2, AlertTriangle, AlertCircle } from 'lucide-react'
import BillingTierModal from './BillingTierModal'
import CancelSubscriptionModal from './CancelSubscriptionModal'
import axios from '@/lib/api/axios'
import { AuthContext } from '@/contexts/AuthContext'

const Feature = ({ children }) => (
  <li className="flex items-start gap-2 text-sm">
    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-emerald-600" />
    <span>{children}</span>
  </li>
)

export default function BillingTier({
  tierDetails,
  currentTier,
  setCurrentTier,
  billingDetails,
  onTierUpdate,
}) {
  const [openModal, setOpenModal] = useState(false)
  const [openCancelModal, setOpenCancelModal] = useState(false)
  const [error, setError] = useState(null)
  const authContext = useContext(AuthContext)

  const handleTierUpdate = newTier => {
    setCurrentTier(newTier)
    onTierUpdate()
  }

  const handleCancelSubscription = async () => {
    try {
      await axios(
        `/organization/${authContext.user.organization.id}/cancel-subscription`,
        { method: 'POST' },
        authContext,
        true,
        'billing',
      )
      setOpenCancelModal(false)
      onTierUpdate()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel subscription')
    }
  }

  const renderTierFeatures = tier => (
    <ul className="space-y-1.5">
      <Feature>{tier.inputTokenLimit.toLocaleString()} input tokens</Feature>
      <Feature>{tier.outputTokenLimit.toLocaleString()} output tokens</Feature>
      <Feature>{tier.integrationLimit} integrations included</Feature>
      {tier.minuteLimit > 0 && (
        <Feature>{tier.minuteLimit} minutes of transcription</Feature>
      )}
      {tier.team && (
        <Feature>Team plan (minimum {tier.minTeamSize} users)</Feature>
      )}
    </ul>
  )

  const currentTierData = tierDetails?.find(tier => tier.name === currentTier)

  return (
    <Card className="gap-3 py-4">
      <CardContent className="px-4">
        <h6 className="text-base font-semibold mb-2">Current Billing Tier</h6>

        {error && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="size-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {billingDetails?.canceledAtEnd && (
          <>
            <Alert className="mb-3 border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              <AlertTriangle className="size-4" />
              <AlertDescription>
                Your subscription has been cancelled and will finish on{' '}
                {new Date(billingDetails.canceledAt).toLocaleDateString()}.
              </AlertDescription>
            </Alert>
            <Separator className="my-3" />
          </>
        )}

        <div>
          <p className="text-xl font-semibold">{currentTier}</p>
          {currentTierData.price > 0 && (
            <p className="mt-1 text-2xl font-bold">
              ${currentTierData.price}/month
              {currentTierData.team && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  per user
                </span>
              )}
            </p>
          )}
        </div>

        {billingDetails?.teamSize > 1 && (
          <>
            <Separator className="my-3" />
            <p className="text-sm">
              Team Size: <b>{billingDetails?.teamSize} users</b>
            </p>
          </>
        )}

        <Separator className="my-3" />
        {renderTierFeatures(currentTierData)}

        {billingDetails?.status === 'canceled' && (
          <Alert className="mt-3 border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
            <AlertTriangle className="size-4" />
            <AlertDescription>
              Your subscription will end on{' '}
              {new Date(billingDetails.canceledAt).toLocaleDateString()}. After
              this date, you will no longer have access to AI features,
              integrations, and transcription minutes.
            </AlertDescription>
          </Alert>
        )}

        <div className="mt-4 flex gap-2">
          <Button onClick={() => setOpenModal(true)}>
            {billingDetails?.canceledAtEnd ? 'Reactivate' : 'Manage'}
          </Button>
          {!billingDetails?.canceledAtEnd && currentTierData.price > 0 && (
            <Button
              variant="outline"
              onClick={() => setOpenCancelModal(true)}
              className="border-destructive text-destructive hover:bg-destructive/10"
            >
              Cancel
            </Button>
          )}
        </div>
      </CardContent>

      <BillingTierModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        currentTier={currentTier}
        billingDetails={billingDetails}
        onTierUpdate={handleTierUpdate}
      />
      <CancelSubscriptionModal
        open={openCancelModal}
        onClose={() => setOpenCancelModal(false)}
        onCancel={handleCancelSubscription}
      />
    </Card>
  )
}
